import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, IconButton, Box } from '@mui/material';
import { Close, ArrowForward } from '@mui/icons-material';
import SelectInput from '../SelectInput';
import { type ColumnType, getColumnTypeFromKey } from '../../common/utils/column-type.util';

interface MergeColumnsDialogProps {
  open: boolean;
  onClose: () => void;
  onMerge: (sourceColumn: string, destinationColumn: string) => void;
  columns: string[];
  fieldHeadersMapping: Record<string, string>;
  rows: any[];
}

const getColumnType = (columnKey: string, fieldHeadersMapping: Record<string, string>, rows: any[]): ColumnType => {
  return getColumnTypeFromKey(columnKey, fieldHeadersMapping, rows);
};

const MergeColumnsDialog: React.FC<MergeColumnsDialogProps> = ({ open, onClose, onMerge, columns, fieldHeadersMapping, rows }) => {
  const [sourceColumn, setSourceColumn] = useState<string>('');
  const [destinationColumn, setDestinationColumn] = useState<string>('');

  // Get column type for source column
  const sourceColumnType = useMemo<ColumnType | null>(() => {
    if (!sourceColumn) return null;
    return getColumnType(sourceColumn, fieldHeadersMapping, rows);
  }, [sourceColumn, fieldHeadersMapping, rows]);

  // Filter destination columns based on source column type
  const validDestinationColumns = useMemo(() => {
    const excluded = new Set(['id', 'division', sourceColumn].filter(Boolean));
    if (!sourceColumn || !sourceColumnType) return columns.filter(col => !excluded.has(col));

    return columns.filter(col => {
      if (excluded.has(col)) return false;
      const destType = getColumnType(col, fieldHeadersMapping, rows);
      return destType === sourceColumnType;
    });
  }, [columns, sourceColumn, sourceColumnType, fieldHeadersMapping, rows]);

  const handleMerge = () => {
    if (!sourceColumn || !destinationColumn) {
      return;
    }
    onMerge(sourceColumn, destinationColumn);
    setSourceColumn('');
    setDestinationColumn('');
    onClose();
  };

  const handleClose = () => {
    setSourceColumn('');
    setDestinationColumn('');
    onClose();
  };

  const getColumnDisplayName = (columnKey: string) => {
    return fieldHeadersMapping[columnKey] || columnKey;
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Merge Columns
        <IconButton onClick={handleClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Select a source column and destination column to merge. Numbers will be added together, and text will be concatenated.
          </Typography>

          <Stack direction='row' spacing={2} alignItems='flex-end' sx={{ width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <SelectInput
                label='Source Column'
                selected={sourceColumn || ''}
                setSelected={value => {
                  setSourceColumn(value);
                  setDestinationColumn(''); // Reset destination when source changes
                }}
                list={[
                  { value: '', label: 'Select source column' },
                  ...columns
                    .filter(col => col !== 'id' && col !== 'division')
                    .map(col => ({
                      value: col,
                      label: getColumnDisplayName(col),
                    })),
                ]}
                variant='standard'
                fullWidth
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                px: 2,
                pb: 0.5,
                minHeight: '56px',
              }}
            >
              <ArrowForward sx={{ color: 'primary.main', fontSize: 32, fontWeight: 'bold' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SelectInput
                label='Destination Column'
                selected={destinationColumn || ''}
                setSelected={value => setDestinationColumn(value)}
                list={[
                  { value: '', label: 'Select destination column' },
                  ...validDestinationColumns.map(col => ({
                    value: col,
                    label: getColumnDisplayName(col),
                  })),
                ]}
                variant='standard'
                fullWidth
                disabled={!sourceColumn || validDestinationColumns.length === 0}
              />
            </Box>
          </Stack>

          {sourceColumn && (
            <Box>
              <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
                Column Type: <strong>{sourceColumnType === 'number' ? 'Number' : 'Text'}</strong>
              </Typography>
              {validDestinationColumns.length === 0 && (
                <Typography variant='caption' color='error' sx={{ display: 'block' }}>
                  No compatible columns found. Please select a different source column.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleMerge}
          variant='contained'
          color='primary'
          disabled={!sourceColumn || !destinationColumn || validDestinationColumns.length === 0}
          sx={{
            color: 'white !important',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Merge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeColumnsDialog;
